import { useRef, useState, type CSSProperties } from "react";
import { Check } from "lucide-react";

import { useMatrix } from "~/context/MatrixContext";
import {
  parseMessageActions,
  sendActionResponse,
  type ActionResponse,
  type MessageActionButton,
  type MessageActionStyle,
} from "~/lib/matrix";
import { memberKind } from "~/lib/member-kind";
import { ICON_STROKE } from "~/lib/icon";

const MAX_OUTCOME_LINES = 3;

interface MessageActionsBarProps {
  roomId: string;
  eventId: string;
  senderId: string;
  content: unknown;
  responses?: ActionResponse[];
  disabled?: boolean;
}

const buttonBase: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "var(--chat-space-1)",
  height: 28,
  padding: "0 var(--chat-space-3)",
  borderRadius: "var(--chat-radius-sm)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-sm-size)",
  lineHeight: "var(--chat-type-body-sm-line)",
  fontWeight: 500,
  border: "1px solid transparent",
};

const buttonStyles: Record<MessageActionStyle, CSSProperties> = {
  primary: { background: "var(--chat-cta-bg)", color: "var(--chat-cta-fg)" },
  danger: {
    background: "transparent",
    color: "var(--chat-state-danger)",
    borderColor: "color-mix(in srgb, var(--chat-state-danger) 30%, transparent)",
  },
  default: { background: "var(--chat-surface-raised)", color: "var(--chat-fg-primary)" },
};

export function MessageActionsBar({
  roomId,
  eventId,
  senderId,
  content,
  responses = [],
  disabled = false,
}: MessageActionsBarProps) {
  const { client, userId } = useMatrix();
  const [pendingChoice, setPendingChoice] = useState<string | null>(null);
  const sending = useRef(false);
  const actions = memberKind(senderId) === "agent" ? parseMessageActions(content) : null;
  if (!actions) return null;

  const ownResponse = responses.find((response) => response.sender.id === userId);
  const chosen = ownResponse?.actionId ?? pendingChoice;
  const outcomes: ActionResponse[] =
    pendingChoice && !ownResponse && userId
      ? [
          ...responses,
          { actionId: pendingChoice, sender: { id: userId, displayName: "" }, ts: Date.now() },
        ]
      : responses;
  const locked = disabled || chosen !== null || !client;

  const choose = async (button: MessageActionButton) => {
    if (locked || !client || sending.current) return;
    sending.current = true;
    setPendingChoice(button.id);
    try {
      await sendActionResponse(client, roomId, eventId, button.id);
    } catch {
      sending.current = false;
      setPendingChoice(null);
    }
  };

  const labelFor = (actionId: string) =>
    actions.find((action) => action.id === actionId)?.label ?? actionId;
  const visible = outcomes.slice(0, MAX_OUTCOME_LINES);
  const hidden = outcomes.length - visible.length;

  return (
    <div style={{ marginTop: "var(--chat-space-2)" }}>
      <div
        role="group"
        aria-label="Message actions"
        style={{ display: "flex", flexWrap: "wrap", gap: "var(--chat-space-2)" }}
      >
        {actions.map((button) => (
          <button
            key={button.id}
            type="button"
            className="chat-button"
            disabled={locked}
            aria-pressed={chosen === button.id}
            onClick={() => void choose(button)}
            style={{
              ...buttonBase,
              ...buttonStyles[button.style],
              cursor: locked ? "default" : "pointer",
              opacity: locked && chosen !== button.id ? 0.5 : 1,
            }}
          >
            {chosen === button.id && <Check size={14} strokeWidth={ICON_STROKE} aria-hidden />}
            {button.label}
          </button>
        ))}
      </div>
      {outcomes.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            margin: "var(--chat-space-1) 0 0",
            padding: 0,
            color: "var(--chat-fg-muted)",
            fontSize: "var(--chat-type-body-xs-size)",
            lineHeight: "var(--chat-type-body-xs-line)",
            fontWeight: "var(--chat-type-body-xs-weight)",
          }}
        >
          {visible.map((response, index) => (
            <li key={`${response.sender.id}-${response.ts}-${index}`}>
              {response.sender.id === userId ? "You" : response.sender.displayName} chose{" "}
              {labelFor(response.actionId)}
            </li>
          ))}
          {hidden > 0 && <li>+{hidden} more</li>}
        </ul>
      )}
    </div>
  );
}
